import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { processLayoutKeys } from '../query-keys';
import {
  getProcessLayoutContract,
  type FlowPositionItem,
  type GetProcessLayoutResponse,
  type ProcessLayoutScope
} from '@bliks-io/contracts';

export const processLayoutQueryOptions = (processId: string, scope: ProcessLayoutScope) =>
  queryOptions({
    queryKey: processLayoutKeys.detail(processId, scope),
    queryFn: async ({ signal }): Promise<GetProcessLayoutResponse> => {
      const { data } = await apiClient.get<GetProcessLayoutResponse>(
        getProcessLayoutContract.path.replace(':processId', processId),
        { params: { scope }, signal }
      );
      return data;
    },
    staleTime: 30_000
  });

/**
 * Cancel any in-flight layout fetch for `scope` and load a fresh one.
 * `invalidate` isn't enough: a GET that returned (or is returning) the empty
 * `{ updatedAt: null }` shape — issued before the backend seeded (draft
 * create) or promoted (publish) the layout — can be served to the active
 * observer post-invalidate, stranding the flow with an empty position map.
 * Cancelling first guarantees the following `staleTime: 0` fetch is the
 * authoritative result.
 *
 * Used on draft create + editor entry (`draft`), and after publish/discard
 * (`published`, which the backend promotes from the draft layout in the same
 * transaction).
 */
export async function seedLayout(
  queryClient: QueryClient,
  processId: string,
  scope: ProcessLayoutScope
): Promise<void> {
  await queryClient.cancelQueries({
    queryKey: processLayoutKeys.detail(processId, scope)
  });
  await queryClient.fetchQuery({
    ...processLayoutQueryOptions(processId, scope),
    staleTime: 0
  });
}

/**
 * Options object rather than a bare flag, matching `@bliks-io/app-sdk`'s
 * `useProcessLayout` — same name, same position, so the two signatures cannot
 * disagree about what the third argument means.
 */
export function useProcessLayout(
  processId: string,
  scope: ProcessLayoutScope = 'draft',
  options?: { enabled?: boolean }
) {
  return useQuery({
    ...processLayoutQueryOptions(processId, scope),
    enabled: options?.enabled ?? true
  });
}

/**
 * Flatten `{stepPositions, generatedNodePositions}` into the unified
 * `FlowPositionItem[]` shape consumed by `ProcessFlowVisualization`.
 */
export function layoutToSavedPositions(
  layout: GetProcessLayoutResponse | undefined
): FlowPositionItem[] | undefined {
  if (!layout) return undefined;
  return [
    ...layout.stepPositions.map((p) => ({
      stepId: String(p.stableStepId),
      x: p.x,
      y: p.y
    })),
    ...layout.generatedNodePositions.map((g) => ({
      stepId: g.nodeId,
      x: g.x,
      y: g.y
    }))
  ];
}
